import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { Download, Mail, Share2 } from 'lucide-react';
import PdfPreviewModal from '../components/PdfPreviewModal';
import { downloadPdf } from '../utils/downloadPdf';

const RapportDigital = () => {
  const { id } = useParams();
  const [rapport, setRapport] = useState(null);
  const [loading, setLoading] = useState(true);
  const [pdfPreviewOpen, setPdfPreviewOpen] = useState(false);

  const pdfFilename = `rapport_ticfe_${rapport?.siren || 'client'}_${new Date().toISOString().split('T')[0]}.pdf`;

  useEffect(() => {
    loadRapport();
  }, [id]);

  const loadRapport = async () => {
    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/rapports/${id}`);
      const data = await response.json();
      setRapport(data);
    } catch (error) {
      alert('Erreur chargement rapport');
    } finally {
      setLoading(false);
    }
  };

  const downloadPDF = async () => {
    const filename = `rapport_ticfe_${rapport?.siren || 'client'}_${new Date().toISOString().split('T')[0]}.pdf`;
    try {
      await downloadPdf({ url: `/api/rapports/${id}/pdf`, filename });
    } catch { /* toast géré par l'utilitaire */ }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#F5F6F9]">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#397DFF]"></div>
      </div>
    );
  }

  if (!rapport) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#F5F6F9]">
        <div className="text-center">
          <h1 className="text-2xl font-heading font-semibold text-[#191D1E] mb-2">Rapport non trouvé</h1>
          <p className="text-[#687175]">Ce rapport n'existe pas ou a été supprimé</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#F5F6F9]">
      {/* Header sticky */}
      <div className="sticky top-0 z-50 bg-white border-b border-[#BFC6CC] shadow-sm">
        <div className="max-w-5xl mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <img src="/logo.jpg" alt="Place des Énergies" className="h-12" />
            <div>
              <h1 className="font-heading font-semibold text-[#191D1E]">Rapport d'Éligibilité TICFE</h1>
              <p className="text-sm text-[#687175]">{rapport.nom_entreprise}</p>
            </div>
          </div>
          <div className="flex gap-3">
            <button
              onClick={() => setPdfPreviewOpen(true)}
              className="flex items-center gap-2 border border-[#397DFF] text-[#397DFF] hover:bg-[#397DFF] hover:text-white px-4 py-2 rounded-lg transition-colors"
              data-testid="download-pdf-btn"
            >
              <Download className="w-4 h-4" /> Télécharger PDF
            </button>
            <button
              className="flex items-center gap-2 bg-[#397DFF] hover:bg-[#4672B6] text-white px-4 py-2 rounded-lg"
            >
              <Share2 className="w-4 h-4" /> Partager
            </button>
          </div>
        </div>
      </div>

      {/* Contenu rapport */}
      <div className="max-w-5xl mx-auto px-6 py-8 space-y-8">
        
        {/* 1. Informations Client */}
        <section className="bg-white border border-[#BFC6CC] rounded-lg p-8">
          <h2 className="text-2xl font-heading font-semibold text-[#191D1E] mb-6">
            Informations Client
          </h2>
          
          <div className="grid grid-cols-2 gap-6">
            <div>
              <div className="text-sm text-[#687175] mb-1">Entreprise</div>
              <div className="font-semibold text-[#191D1E]">{rapport.nom_entreprise}</div>
            </div>
            <div>
              <div className="text-sm text-[#687175] mb-1">SIREN</div>
              <div className="font-mono font-semibold text-[#191D1E]">{rapport.siren}</div>
            </div>
            <div>
              <div className="text-sm text-[#687175] mb-1">Période Fiscale</div>
              <div className="text-[#191D1E]">
                {new Date(rapport.debut_periode).toLocaleDateString('fr-FR')} au {new Date(rapport.fin_periode).toLocaleDateString('fr-FR')}
              </div>
            </div>
            <div>
              <div className="text-sm text-[#687175] mb-1">Valeur Ajoutée</div>
              <div className="font-mono font-semibold text-[#191D1E]">
                {rapport.valeur_ajoutee.toLocaleString('fr-FR')} €
              </div>
            </div>
            <div>
              <div className="text-sm text-[#687175] mb-1">Date d'Analyse</div>
              <div className="text-[#191D1E]">
                {new Date(rapport.date_analyse).toLocaleDateString('fr-FR')}
              </div>
            </div>
            <div>
              <div className="text-sm text-[#687175] mb-1">Energy Manager</div>
              <div className="text-[#191D1E]">{rapport.energy_manager}</div>
            </div>
          </div>

          {/* PDL */}
          <div className="mt-6 pt-6 border-t border-[#BFC6CC]">
            <div className="text-sm text-[#687175] mb-3">Points De Livraison</div>
            <div className="space-y-2">
              {rapport.pdls_data?.map((pdl, index) => (
                <div key={index} className="flex items-center gap-4 p-3 bg-[#F5F6F9] rounded-lg">
                  <div className="font-mono font-semibold text-[#191D1E]">{pdl.pdl}</div>
                  <div className="text-sm text-[#687175]">
                    {pdl.puissance_souscrite} kVA • {pdl.code_naf}
                  </div>
                  <div className="ml-auto text-sm text-[#397DFF]">
                    {pdl.total_conso_mwh?.toFixed(2)} MWh
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* 2. Introduction TICFE */}
        <section className="bg-white border border-[#BFC6CC] rounded-lg p-8">
          <h2 className="text-2xl font-heading font-semibold text-[#191D1E] mb-6">
            Qu'est-ce que la TICFE ?
          </h2>
          
          <div className="space-y-4 text-[#687175] leading-relaxed">
            <p>
              Autrement appelée Accise sur l'Energie ou CSPE (Contribution au Service Public d'Electricité), 
              la Taxe Intérieure sur la Consommation Finale d'Electricité est due par tous les consommateurs 
              d'électricité au prorata de leur consommation.
            </p>
            <p>
              Il s'agit d'une taxe figurant sur la facture d'électricité.
            </p>
            <p>
              Bien que cette taxe soit gérée, recouvrée et contrôlée par l'administration fiscale depuis 2022, 
              ce sont les fournisseurs d'électricité qui sont en charge de percevoir les montants et de les 
              restituer à l'administration fiscale ensuite.
            </p>
          </div>
        </section>

        {/* 3. Situation Actuelle */}
        <section className="bg-white border border-[#BFC6CC] rounded-lg p-8">
          <h2 className="text-2xl font-heading font-semibold text-[#191D1E] mb-6">
            Votre Situation Actuelle
          </h2>
          
          <div className="grid grid-cols-2 gap-6">
            <div className="p-4 bg-[#F5F6F9] rounded-lg">
              <div className="text-sm text-[#687175] mb-2">Consommation Totale</div>
              <div className="text-2xl font-heading font-bold text-[#191D1E]">
                {rapport.pdls_data?.reduce((sum, p) => sum + (p.total_conso_mwh || 0), 0).toFixed(2)} MWh
              </div>
            </div>
            <div className="p-4 bg-[#F5F6F9] rounded-lg">
              <div className="text-sm text-[#687175] mb-2">TICFE Actuelle (CERFA)</div>
              <div className="text-2xl font-heading font-bold text-[#191D1E]">
                {rapport.pdls_data?.reduce((sum, p) => sum + (p.total_ticfe_cerfa || 0), 0).toLocaleString('fr-FR')} €
              </div>
            </div>
            <div className="p-4 bg-[#E8F4FD] rounded-lg border border-[#397DFF]">
              <div className="text-sm text-[#687175] mb-2">Ratio TICFE / Valeur Ajoutée</div>
              <div className="text-2xl font-heading font-bold text-[#397DFF]">
                {(rapport.ratio_global * 100).toFixed(3)} %
              </div>
            </div>
            <div className="p-4 bg-[#E8F4FD] rounded-lg border border-[#397DFF]">
              <div className="text-sm text-[#687175] mb-2">Taux Optimisé</div>
              <div className="text-2xl font-heading font-bold text-[#397DFF]">
                {rapport.taux_optimise_moyen} €/MWh
              </div>
            </div>
          </div>
        </section>

        {/* 4. Constat Éligibilité */}
        <section className={`border-2 rounded-lg p-8 ${
          rapport.eligibilite_globale === 'ELIGIBLE' 
            ? 'bg-[#10D183]/5 border-[#10D183]' 
            : 'bg-red-50 border-red-300'
        }`}>
          <h2 className="text-2xl font-heading font-semibold text-[#191D1E] mb-6">
            Résultat d'Éligibilité
          </h2>
          
          <div className="flex items-center gap-4 mb-6">
            <div className={`text-5xl font-heading font-bold ${
              rapport.eligibilite_globale === 'ELIGIBLE' ? 'text-[#10D183]' : 'text-red-600'
            }`}>
              {rapport.eligibilite_globale}
            </div>
            {rapport.eligibilite_globale === 'ELIGIBLE' && (
              <div className="text-[#191D1E]">
                au tarif réduit de TICFE
              </div>
            )}
          </div>

          <div className="p-4 bg-white rounded-lg border border-[#BFC6CC]">
            <div className="text-sm text-[#687175]">
              Votre ratio TICFE/VA de <strong className="text-[#191D1E]">{(rapport.ratio_global * 100).toFixed(3)}%</strong>
              {rapport.ratio_global >= 0.005 ? ' est supérieur' : ' est inférieur'} au seuil minimum de 0,5%.
            </div>
          </div>
        </section>

        {/* 5. Économies - À développer */}
        {rapport.eligibilite_globale === 'ELIGIBLE' && (
          <section className="bg-white border border-[#BFC6CC] rounded-lg p-8">
            <h2 className="text-2xl font-heading font-semibold text-[#191D1E] mb-6">
              Vos Économies Potentielles
            </h2>
            
            <div className="space-y-6">
              {/* Attestation */}
              <div>
                <h3 className="text-lg font-heading font-semibold text-[#191D1E] mb-3">
                  Système d'Attestation (12 prochains mois)
                </h3>
                <div className="p-6 bg-[#E8F4FD] border-2 border-[#397DFF] rounded-lg text-center">
                  <div className="text-4xl font-heading font-bold text-[#397DFF]">
                    {rapport.economies_attestation_total?.toLocaleString('fr-FR')} €
                  </div>
                </div>
              </div>

              {/* Remboursement */}
              <div>
                <h3 className="text-lg font-heading font-semibold text-[#191D1E] mb-3">
                  Système de Remboursement
                </h3>
                <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                  {Object.entries(rapport.economies_remboursement || {}).map(([annee, montant]) => (
                    <div key={annee} className="p-4 bg-[#F5F6F9] border border-[#BFC6CC] rounded-lg text-center">
                      <div className="text-sm text-[#687175] mb-2">{annee}</div>
                      <div className="text-xl font-heading font-bold text-[#397DFF]">
                        {montant.toLocaleString('fr-FR')} €
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </section>
        )}

        {/* 6. Contact */}
        <section className="bg-[#191D1E] text-white rounded-lg p-8">
          <h2 className="text-2xl font-heading font-semibold mb-6">
            Nous Contacter
          </h2>
          
          {rapport.commercial_id ? (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <div className="text-sm text-[#BFC6CC] mb-1">Votre Conseiller</div>
                <div className="font-semibold">{rapport.commercial?.prenom} {rapport.commercial?.nom}</div>
              </div>
              <div>
                <div className="text-sm text-[#BFC6CC] mb-1">Email</div>
                <a href={`mailto:${rapport.commercial?.email}`} className="text-[#397DFF] hover:text-[#4672B6]">
                  {rapport.commercial?.email}
                </a>
              </div>
              <div>
                <div className="text-sm text-[#BFC6CC] mb-1">Téléphone</div>
                <a href={`tel:${rapport.commercial?.telephone}`} className="text-white hover:text-[#BFC6CC]">
                  {rapport.commercial?.telephone}
                </a>
              </div>
              <div>
                <div className="text-sm text-[#BFC6CC] mb-1">Agence</div>
                <div>{rapport.commercial?.agence}</div>
              </div>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <div className="text-sm text-[#BFC6CC] mb-1">Votre Conseiller</div>
                <div className="font-semibold">{rapport.energy_manager}</div>
              </div>
              <div>
                <div className="text-sm text-[#BFC6CC] mb-1">Email</div>
                <a href="mailto:energymanager@cockpit.placedesenergies.com" className="text-[#397DFF] hover:text-[#4672B6]">
                  energymanager@cockpit.placedesenergies.com
                </a>
              </div>
            </div>
          )}

          <div className="mt-6 pt-6 border-t border-[#687175]">
            <p className="text-sm text-[#BFC6CC]">
              Place des Énergies - 5 rue des indes noires, 80440 Boves
            </p>
          </div>
        </section>
      </div>

      <PdfPreviewModal
        open={pdfPreviewOpen}
        onClose={() => setPdfPreviewOpen(false)}
        url={`${process.env.REACT_APP_BACKEND_URL}/api/rapports/${id}/pdf`}
        title={`Rapport TICFE — ${rapport?.nom_entreprise || rapport?.siren || ''}`}
        filename={pdfFilename}
      />
    </div>
  );
};

export default RapportDigital;
